import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from 'reactstrap';
import { PlusCircle, Edit, Trash2, Image as ImageIcon } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingBanner, fetchUploadBanner, fetchEditBanner, fetchDeleteBanner } from '../../../../api/banner';
import { toast } from 'react-toastify';

const BannerManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Load banners khi component mount
  useEffect(() => {
    loadBanners();
  }, []);

  // Hàm load danh sách banner
  const loadBanners = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingBanner();
      setBanners(data);
      toast.success('Tải danh sách banner thành công!');
    } catch (error) {
      toast.error('Lỗi khi tải banner: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chọn file
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentBanner(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (banner) => {
    setEditMode(true);
    setCurrentBanner(banner);
    setPreviewUrl(banner.url);
    setSelectedFile(null);
    setModalOpen(true);
  };

  // Xử lý upload/edit banner
  const handleSubmit = async () => {
    if (!selectedFile && !editMode) {
      toast.warning('Vui lòng chọn file ảnh!');
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentBanner) {
        // Edit banner
        const formData = new FormData();
        if (selectedFile) {
          formData.append('url', selectedFile);
        }
        await fetchEditBanner(currentBanner._id, formData);
        toast.success('Cập nhật banner thành công!');
      } else {
        // Upload new banner
        await fetchUploadBanner(selectedFile);
        toast.success('Thêm banner mới thành công!');
      }
      
      setModalOpen(false);
      loadBanners();
    } catch (error) {
      toast.error('Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa banner
  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa banner này?')) {
      setLoading(true);
      try {
        await fetchDeleteBanner(id);
        toast.success('Xóa banner thành công!');
        loadBanners();
      } catch (error) {
        toast.error('Lỗi khi xóa banner: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Banner" title="Banner Management" mainTitle="Quản lý Banner" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4>Danh sách Banner</h4>
                  <Button color="primary" onClick={handleAddNew}>
                    <PlusCircle size={16} className="me-2" />
                    Thêm Banner Mới
                  </Button>
                </div>

                {loading && <div className="text-center my-4">Đang tải...</div>}

                <Row>
                  {banners.length === 0 && !loading && (
                    <Col xs="12">
                      <div className="text-center my-4">
                        <ImageIcon size={64} className="text-muted mb-3" />
                        <p className="text-muted">Chưa có banner nào</p>
                      </div>
                    </Col>
                  )}

                  {banners.map((banner, index) => (
                    <Col xl="3" lg="4" md="6" sm="12" key={banner._id || index}>
                      <Card className="shadow-sm">
                        <div className="position-relative" style={{ paddingTop: '56.25%' }}>
                          <img
                            src={banner.url}
                            alt={`Banner ${index + 1}`}
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              ID: {banner._id?.slice(-6)}
                            </small>
                            <div className="btn-group">
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handleEdit(banner)}
                                className="me-2"
                              >
                                <Edit size={14} />
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleDelete(banner._id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Upload/Edit Banner */}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
          <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
            {editMode ? 'Chỉnh sửa Banner' : 'Thêm Banner Mới'}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="bannerFile">Chọn ảnh Banner</Label>
              <Input
                id="bannerFile"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
            </FormGroup>

            {previewUrl && (
              <div className="mt-3">
                <Label>Xem trước:</Label>
                <div className="border rounded p-2">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-100"
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                  />
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              Hủy
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Đang xử lý...' : editMode ? 'Cập nhật' : 'Thêm mới'}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default BannerManagement;